import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { UtilityService } from '../../service/utility.service';
import { ConfigService } from '../../service/config.service';
import { LandingFeature } from '../../models/PortalConfigurationView';
import {
  PortalFeatureDirective,
  PortalLoginRequiredDirective,
  PortalButtonDirective,
} from '../../directives';

interface Slide {
  image: string;
  alt: string;
}

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    PortalFeatureDirective,
    PortalLoginRequiredDirective,
    PortalButtonDirective,
  ],
  templateUrl: './home.component.html',
  styleUrl: './home.component.css',
})
export class HomeComponent implements OnInit, OnDestroy {
  constructor(
    private utilityService: UtilityService,
    private configService: ConfigService,
  ) {}

  LoggedIn = JSON.parse(localStorage.getItem('login') || 'false');
  slides: Slide[] = [];
  currentSlide = 0;
  nextSlideIndex = 0;
  isAnimating = false;
  private configSub?: Subscription;
  private slideTimer?: ReturnType<typeof setInterval>;

  ngOnInit(): void {
    this.refreshSlides();
    this.configSub = this.configService.getConfig$().subscribe(() => {
      this.refreshSlides();
    });
  }

  ngOnDestroy(): void {
    if (this.configSub) this.configSub.unsubscribe();
    if (this.slideTimer) clearInterval(this.slideTimer);
  }

  private refreshSlides(): void {
    this.slides = this.resolveSlides();
    this.currentSlide = 0;
    this.nextSlideIndex = this.slides.length > 1 ? 1 : 0;
    if (this.slideTimer) {
      clearInterval(this.slideTimer);
      this.slideTimer = undefined;
    }
    this.autoSlide();
  }

  private resolveSlides(): Slide[] {
    const json = this.utilityService.portalConfig?.portalHeroImages;
    if (json) {
      try {
        const parsed = JSON.parse(json);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const slides = parsed
            .filter(
              (item: any) => item && typeof item.url === 'string' && item.url,
            )
            .map((item: any) => ({
              image: item.url,
              alt: item.fileName || 'Hero image',
            }));
          if (slides.length > 0) return slides;
        }
      } catch {
        // fall through to defaults
      }
    }
    return this.getDefaultSlides();
  }

  private getDefaultSlides(): Slide[] {
    return [
      { image: '../../assets/img/banner/body_background.png', alt: 'Slide 1' },
      { image: '../../assets/img/banner/body_background2.jpg', alt: 'Slide 2' },
      { image: '../../assets/img/banner/body_background4.jpg', alt: 'Slide 3' },
      { image: '../../assets/img/banner/body_background5.jpg', alt: 'Slide 4' },
    ];
  }

  nextSlide(): void {
    if (this.isAnimating || this.slides.length < 2) return;
    this.isAnimating = true;
    this.nextSlideIndex = (this.currentSlide + 1) % this.slides.length;

    setTimeout(() => {
      this.currentSlide = this.nextSlideIndex;
      this.nextSlideIndex = (this.currentSlide + 1) % this.slides.length;
      this.isAnimating = false;
    }, 600);
  }

  prevSlide(): void {
    if (this.isAnimating || this.slides.length < 2) return;
    this.isAnimating = true;
    this.nextSlideIndex =
      (this.currentSlide - 1 + this.slides.length) % this.slides.length;

    setTimeout(() => {
      this.currentSlide = this.nextSlideIndex;
      this.nextSlideIndex = (this.currentSlide + 1) % this.slides.length;
      this.isAnimating = false;
    }, 600);
  }

  goToSlide(index: number): void {
    if (this.isAnimating || index === this.currentSlide) return;
    this.isAnimating = true;
    this.nextSlideIndex = index;

    setTimeout(() => {
      this.currentSlide = this.nextSlideIndex;
      this.nextSlideIndex = (this.currentSlide + 1) % this.slides.length;
      this.isAnimating = false;
    }, 600);
  }

  autoSlide(): void {
    if (this.slides.length > 1) {
      this.slideTimer = setInterval(() => this.nextSlide(), 4000);
    }
  }

  get welcomeParagraphs(): string[] {
    const content = this.utilityService.portalConfig?.portalLandingContent;
    if (content && content.trim()) {
      return content
        .split(/\r?\n/)
        .map((p) => p.trim())
        .filter((p) => p.length > 0);
    }
    return this.getDefaultWelcomeParagraphs();
  }

  private getDefaultWelcomeParagraphs(): string[] {
    return [
      'Welcome the Vendor Portal for the City of St. Pete Beach. New vendors who have just begun providing goods or services to the city or who wish to do business with the city in the future are encouraged to officially register with the city by clicking on the New Vendor Registration link and completing the registration form. Under Vendor Type, Active indicates a vendor that is currently or imminently engaging with the City in business transactions. Prospective, on the other hand, indicates that your entity is not currently engaged with the City but wishes or intends to do business with the City at some point in the future.. Be sure to click on Contact to add your contact information including email address within the form.',
      'If you are an existing vendor currently doing business with the City of St Pete Beach, please submit any invoices requiring payment by the city by clicking on the ‘Vendor Invoice Submission’ link. If there is a purchase order associated with the goods or services you are providing in the city, please enter your purchase order number in the applicable space. If you are unsure of the purchase order number associated with the goods or services you are delivering, you can contact the Finance Department, at AP@stpetebeach.org. Be sure to attach your invoice and any other pertinent documentation when submitting.',
      'We would like to thank you for doing business with the City of St Pete Beach, either currently or in the future. By entering this pertinent information into the New Vendor Registration portal or the Vendor Invoice Submission portal, we will be able to communicate new business opportunities to you timely and streamline the invoice submission process so that you can be paid quickly and accurately. We look forward to your continued engagement with the city.',
    ];
  }

  isFeatureVisible(index: 0 | 1 | 2): boolean {
    const features = this.landingFeatures;
    if (!features[index]?.enabled) return false;

    const cfg = this.utilityService.portalConfig;
    if (!cfg) return true;

    const flag =
      index === 0
        ? cfg.registrationFeatureEnabled
        : index === 1
          ? cfg.signupAndProfilingEnabled
          : cfg.invoiceFeatureEnabled;
    return String(flag).toLowerCase() !== 'false';
  }

  get landingFeatures(): LandingFeature[] {
    const json = this.utilityService.portalConfig?.landingFeatures;
    const defaults = this.getDefaultLandingFeatures();
    if (!json) return defaults;
    try {
      const parsed = JSON.parse(json);
      if (!Array.isArray(parsed) || parsed.length !== 3) return defaults;
      return parsed.map((f: any, i: number) => ({
        enabled: typeof f?.enabled === 'boolean' ? f.enabled : true,
        title: f?.title || defaults[i].title,
        content: f?.content || defaults[i].content,
        buttonText: f?.buttonText || defaults[i].buttonText,
      }));
    } catch {
      return defaults;
    }
  }

  private getDefaultLandingFeatures(): LandingFeature[] {
    return [
      {
        enabled: true,
        title: 'New Vendor Registration',
        content:
          'Note: City will receive your registration. Once approved by the city, you will receive an automated email with your vendor ID and can proceed to step 2.',
        buttonText: 'Register Now',
      },
      {
        enabled: true,
        title: 'Profile Setup',
        content:
          'Once you receive your vendor ID, you can proceed with setting up a username and password to submit invoices and monitor invoice and Purchase Order history.',
        buttonText: 'Setup Profile',
      },
      {
        enabled: true,
        title: 'Vendor Invoice Submission',
        content:
          'Once profile is setup, you can begin submitting invoices through the Vendor Invoice Submission page and monitor progress via your profile.',
        buttonText: 'Submit Invoices',
      },
    ];
  }

  // Direct access to portal configuration
  get portalConfig() {
    return this.utilityService.portalConfig;
  }
}
